import { UploaderFileEntry, UploaderPlugin } from './uploader-plugin.types';

/** Add this to a <uc-config> source-list to show the source. */
export const COMPUTER_UPLOAD_SOURCE_ID = 'computer';

const PLUGIN_ID = 'gs-computer-upload';
const ICON_NAME = 'gs-computer';
const LABEL_KEY = 'gs-computer-upload.source';

/** Files the widget would otherwise upload from this computer: its file picker, paste and drop area. */
const LOCAL_SOURCES = ['local', 'drop-area'];

const COMPUTER_ICON =
  '<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" stroke-width="1.6" ' +
  'stroke-linecap="round" stroke-linejoin="round"><rect x="3.5" y="4.5" width="17" height="11" rx="1.5"/>' +
  '<path d="M2 19.5h20"/></svg>';

export interface ComputerUploadPluginOptions {
  /** Opens the page's own file input, which uploads straight to S3 / Mux. */
  openFilePicker(): void;
  /** Receives files dropped or picked inside the widget, for that same direct upload. */
  onFiles(files: File[]): void;
}

function isMediaFile(file: File): boolean {
  return /^(image|video)\//.test(file.type || '');
}

/**
 * Keeps local files off the Uploadcare path. Through the widget, a local file is
 * uploaded to Uploadcare, downloaded back into the browser, then uploaded again
 * to S3 / Mux; the page's own file input sends it once.
 *
 * Adds an "Add from computer" source that opens that input, and takes over files
 * dropped or picked in the widget's drop area: each is removed from the widget
 * (which cancels its Uploadcare upload) and handed to `onFiles`.
 */
export function createComputerUploadPlugin(options: ComputerUploadPluginOptions): UploaderPlugin {
  return {
    id: PLUGIN_ID,
    setup({ pluginApi, uploaderApi }) {
      pluginApi.registry.registerIcon({ name: ICON_NAME, svg: COMPUTER_ICON });
      pluginApi.registry.registerL10n({ en: { [LABEL_KEY]: 'Add from computer' } });

      pluginApi.registry.registerSource({
        id: COMPUTER_UPLOAD_SOURCE_ID,
        label: LABEL_KEY,
        icon: ICON_NAME,
        onSelect() {
          uploaderApi.setModalState(false);
          options.openFilePicker();
        }
      });

      // A drop of several files fires one event per file; hand them over together.
      let pending: UploaderFileEntry[] = [];
      const flush = () => {
        const entries = pending;
        pending = [];
        // Removed after the add event finishes, so the uploader isn't mid-add.
        entries.forEach(entry => uploaderApi.removeFileByInternalId(entry.internalId));
        uploaderApi.setModalState(false);
        options.onFiles(entries.map(entry => entry.file as File));
      };

      return uploaderApi.on('file-added', entry => {
        if (!entry || !LOCAL_SOURCES.includes(entry.source) || !(entry.file instanceof File) || !isMediaFile(entry.file)) {
          // Anything else, including unsupported types, stays with the widget and its own errors.
          return;
        }
        if (!pending.length) {
          Promise.resolve().then(flush);
        }
        pending.push(entry);
      });
    }
  };
}
